import { useNavigate } from 'react-router-dom'
import { CONTENT_TYPES } from '../../constants/contentTypes'
import UserTypeDisplay from '../common/UserTypeDisplay'
import useProductCategories from '../../hooks/useProductCategories'
import {
  ShoppingBagIcon,
  UserIcon,
  BuildingStorefrontIcon,
  MagnifyingGlassIcon,
  PhoneIcon,
  TagIcon,
} from '@heroicons/react/24/outline'
import USER_TYPE from '../../constants/userType'
import DisplayShopName from '../common/DisplayShopName'

export default function DisplayReportedContent({ content, contentType }) {
  const navigate = useNavigate()
  const { getCategoryNameById } = useProductCategories()

  if (!content) {
    return (
      <div className="bg-background border border-gray-200 rounded-lg p-6">
        <p className="text-gray-500 text-center">No content details available</p>
      </div>
    )
  }

  const handleUserClick = (user) => {
    if (user.userType === USER_TYPE.customer) {
      navigate(`/admin/customers/${user.id}`)
    } else if (user.userType === USER_TYPE.retailer && user.shop?.shopSlug) {
      navigate(`/admin/retailers/${user.shop.shopSlug}`)
    } else if (user.userType === USER_TYPE.wholeseller && user.shop?.shopSlug) {
      navigate(`/admin/wholesellers/${user.shop.shopSlug}`)
    }
  }

  const getContentTypeIcon = () => {
    switch (contentType) {
      case CONTENT_TYPES.productFinder:
        return <MagnifyingGlassIcon className="w-5 h-5 text-gray-600" />
      case CONTENT_TYPES.product:
        return <ShoppingBagIcon className="w-5 h-5 text-gray-600" />
      case CONTENT_TYPES.customer:
        return <UserIcon className="w-5 h-5 text-gray-600" />
      case CONTENT_TYPES.retailer:
      case CONTENT_TYPES.wholeseller:
        return <BuildingStorefrontIcon className="w-5 h-5 text-gray-600" />
      default:
        return <TagIcon className="w-5 h-5 text-gray-600" />
    }
  }

  const getContentTypeTitle = () => {
    return contentType.split('_').join(' ')
  }

  const renderContentDetails = () => {
    switch (contentType) {
      case CONTENT_TYPES.productFinder:
        return (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-600">ID:</span>
              <span className="text-sm text-gray-900">#{content.id}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-600">Category:</span>
              <span className="text-sm text-gray-900">
                {getCategoryNameById(content.categoryId)}
              </span>
            </div>

            {content.description && (
              <div>
                <span className="text-sm font-medium text-gray-600 block mb-2">Description:</span>
                <p className="text-sm text-gray-900 bg-neutral-200-50 p-3 rounded-md border">
                  {content.description}
                </p>
              </div>
            )}

            {content.imageUrl && (
              <div>
                <span className="text-sm font-medium text-gray-600 block mb-2">
                  Attached Image:
                </span>
                <div className="w-40 h-28 bg-neutral-200-50 border border-gray-200 rounded-md overflow-hidden">
                  <img
                    src={content.imageUrl}
                    alt="Product finder request"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            )}
          </div>
        )

      case CONTENT_TYPES.product:
        return (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-600">ID:</span>
              <span className="text-sm text-gray-900">#{content.id}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-600">Category:</span>
              <span className="text-sm text-gray-900">
                {getCategoryNameById(content.categoryId)}
              </span>
            </div>

            <div className="flex gap-4">
              {content.primaryImageUrl && (
                <div className="flex-shrink-0">
                  <div className="w-32 h-24 bg-neutral-200-50 border border-gray-200 rounded-md overflow-hidden">
                    <img
                      src={content.primaryImageUrl}
                      alt={content.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              )}

              <div className="flex-1 min-w-0">
                <div className="mb-3">
                  <h4 className="text-base font-semibold text-gray-900">{content.name}</h4>
                  {content.shopsCount > 0 && (
                    <p className="text-sm text-gray-600">
                      Sold by {content.shopsCount} {content.shopsCount === 1 ? 'shop' : 'shops'}
                    </p>
                  )}
                </div>

                {content.description && (
                  <div>
                    <p className="text-sm text-gray-900 bg-neutral-200-50 p-3 rounded-md border line-clamp-3">
                      {content.description}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )

      case CONTENT_TYPES.retailer:
      case CONTENT_TYPES.wholeseller:
      case CONTENT_TYPES.customer: {
        const isClickable = content.userType === USER_TYPE.customer

        return (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-600">ID:</span>
              <span className="text-sm text-gray-900">#{content.id}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-600">Type:</span>
              <UserTypeDisplay userType={content.userType} />
            </div>

            <div className="flex gap-2 items-center">
              <span className="text-sm font-medium text-gray-600 block ">Name:</span>
              {isClickable ? (
                <button
                  onClick={() => handleUserClick(content)}
                  className="text-base font-semibold text-blue-600 hover:text-blue-800 hover:underline transition-colors duration-200"
                >
                  {content.name}
                </button>
              ) : (
                <DisplayShopName
                  shop={{
                    name: content?.shop?.shopName,
                    isPremium: content?.shop?.isPremium,
                    isVerified: content?.shop?.isApproved,
                  }}
                />
              )}
            </div>

            {content.phoneNumber && (
              <div className="flex items-center gap-2">
                <PhoneIcon className="w-4 h-4 text-gray-500" />
                <a
                  href={`tel:${content.phoneNumber}`}
                  className="text-sm text-gray-600 hover:text-gray-800 hover:underline transition-colors duration-200"
                >
                  {content.phoneNumber}
                </a>
              </div>
            )}

            {content.shop?.shopSlug && (
              <div>
                <button
                  onClick={() => handleUserClick(content)}
                  className="text-sm text-blue-600 hover:text-blue-800 hover:underline transition-colors duration-200 flex items-center gap-1"
                >
                  <BuildingStorefrontIcon className="w-4 h-4" />
                  View Shop Details
                </button>
              </div>
            )}
          </div>
        )
      }

      default:
        return (
          <div className="text-center py-4">
            <p className="text-sm text-gray-500">Unknown content type</p>
          </div>
        )
    }
  }

  return (
    <div className="mt-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-3">Content</h2>
      <div className="bg-background border border-gray-200 rounded-lg p-4">
        <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-100">
          {getContentTypeIcon()}
          <h3 className="text-base font-medium text-gray-900 capitalize">
            {getContentTypeTitle()}
          </h3>
        </div>
        {renderContentDetails()}
      </div>
    </div>
  )
}
